import React from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "../../utils/cn";

const Checkbox = React.forwardRef(({
    className,
    id,
    checked,
    indeterminate = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    size = "default",
    onChange,
    name,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const checkboxId = id || `checkbox-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Size variants
    const sizeClasses = {
        sm: "h-4 w-4",
        default: "h-4 w-4",
        lg: "h-5 w-5"
    };

    // FIX: Ensure onChange is always a function
    const handleChange = (e) => {
        if (typeof onChange === 'function') {
            onChange(e);
        }
    };

    return (
        <div className={cn("flex items-start space-x-2", className)}>
            <div className="relative flex items-center shrink-0">
                <input
                    type="checkbox"
                    ref={ref}
                    id={checkboxId}
                    name={name || checkboxId}
                    checked={checked}
                    disabled={disabled}
                    required={required}
                    onChange={handleChange}
                    className={cn(
                        "peer shrink-0 rounded border-2 border-gray-300 bg-white",
                        "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        "checked:bg-primary checked:border-primary",
                        "cursor-pointer transition-all duration-200",
                        "relative z-10",
                        sizeClasses?.[size]
                    )}
                    aria-checked={checked}
                    {...props}
                />
                
                {/* Visual check indicator - positioned absolutely but doesn't block clicks */}
                {checked && !indeterminate && (
                    <Check 
                        className={cn(
                            "absolute left-0 top-0 text-white pointer-events-none",
                            size === "sm" ? "h-3 w-3 p-0.5" : size === "lg" ? "h-4 w-4 p-0.5" : "h-3.5 w-3.5 p-0.5"
                        )}
                    />
                )}
                
                {/* Indeterminate indicator */}
                {indeterminate && (
                    <Minus 
                        className={cn(
                            "absolute left-0 top-0 text-white pointer-events-none",
                            size === "sm" ? "h-3 w-3 p-0.5" : size === "lg" ? "h-4 w-4 p-0.5" : "h-3.5 w-3.5 p-0.5"
                        )}
                    />
                )}
            </div>
            
            {(label || description || error) && (
                <div className="flex-1 space-y-1">
                    {label && (
                        <label
                            htmlFor={checkboxId}
                            className={cn(
                                "text-sm font-medium leading-none cursor-pointer select-none",
                                "peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                                error ? "text-destructive" : "text-foreground"
                            )}
                        >
                            {label}
                            {required && <span className="text-destructive ml-1">*</span>}
                        </label>
                    )}

                    {description && !error && (
                        <p className="text-sm text-muted-foreground">
                            {description}
                        </p>
                    )}

                    {error && (
                        <p className="text-sm text-destructive">
                            {error}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
});

Checkbox.displayName = "Checkbox";

// Checkbox Group component
const CheckboxGroup = React.forwardRef(({
    className,
    children,
    label,
    description,
    error,
    required = false,
    disabled = false,
    ...props
}, ref) => {
    return (
        <fieldset
            ref={ref}
            disabled={disabled}
            className={cn("space-y-3", className)}
            {...props}
        >
            {label && (
                <legend className={cn(
                    "text-sm font-medium",
                    error ? "text-destructive" : "text-foreground"
                )}>
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </legend>
            )}

            {description && !error && (
                <p className="text-sm text-muted-foreground">
                    {description}
                </p>
            )}

            <div className="space-y-2">
                {children}
            </div>

            {error && (
                <p className="text-sm text-destructive">
                    {error}
                </p>
            )}
        </fieldset>
    );
});

CheckboxGroup.displayName = "CheckboxGroup";

export { Checkbox, CheckboxGroup };